import { React, useEffect, useMemo, useState } from 'react';

import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import '../css/projectAdjustment.css';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';

const AjusteProjetoConsorcio = () => {
    const [valor, setValor] = useState('');
    const [observacao, setObservacao] = useState('');
    const [projetosList, setProjetosList] = useState([]);
    const [selectedProjetoInicial, setSelectedProjetoInicial] = useState(null);
    const [selectedProjetoFinal, setSelectedProjetoFinal] = useState(null);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const numericValue = Number(valor);
    const hasValidValue = valor !== '' && Number.isFinite(numericValue);
    const adjustmentType = useMemo(() => {
        if (!hasValidValue) {
            return { label: 'Aguardando valor', className: 'is-neutral' };
        }

        return numericValue >= 0
            ? { label: 'Conta a receber', className: 'is-positive' }
            : { label: 'Conta a pagar', className: 'is-negative' };
    }, [hasValidValue, numericValue]);
    const formattedValue = hasValidValue ? formatCurrency(numericValue) : 'R$ --';

    useEffect(() => {
        async function getProjetoData() {
            const response = await fetchFlask('getProjeto');
            if (response.status === 'error') {
                handleAlerta(response);
                return;
            }

            const projetos = response.dados
                .map((item) => ({ CodigoProjeto: item.Codigo, NomeProjeto: item.Nome }));
            setProjetosList(projetos);
        }
        getProjetoData();
    }, []);

    async function submitAjusteProjetoConsorcio (e) {
        e.preventDefault();
        setIsSubmit(true);

        const body = JSON.stringify({
            projeto_inicial: selectedProjetoInicial.CodigoProjeto,
            projeto_final: selectedProjetoFinal.CodigoProjeto,
            valor,
            observacao
        });

        const response = await fetchFlask('ajusteProjetoConsorcio', body);
        handleAlerta(response);
        if (response.status === 'success') {
            setValor('');
            setObservacao('');
            setSelectedProjetoInicial(null);
            setSelectedProjetoFinal(null);
        }
        setIsSubmit(false);
    }

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Ajuste Entre Projetos
            </PageHeader>

            <form className='project-adjustment-layout' action='' method='POST' id='formAjusteProjetoConsorcio' onSubmit={submitAjusteProjetoConsorcio}>
                <section className='project-adjustment-workspace'>
                    <div className='project-adjustment-intro'>
                        <div>
                            <span className='project-adjustment-kicker'>Movimentação interna</span>
                            <h2>Ajuste financeiro entre projetos</h2>
                        </div>
                        <p>Selecione a origem, informe o valor e revise o destino antes de lançar o ajuste.</p>
                    </div>

                    <div className='project-adjustment-flow'>
                        <Autocomplete
                            className='project-adjustment-field'
                            id='projetoInicial'
                            options={projetosList}
                            value={selectedProjetoInicial}
                            onChange={(_, value) => setSelectedProjetoInicial(value)}
                            getOptionLabel={(option) => option.NomeProjeto}
                            isOptionEqualToValue={(option, value) => option.CodigoProjeto === value.CodigoProjeto}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Projeto origem'
                                    required
                                    helperText='Projeto que receberá a baixa ou origem do ajuste.'
                                />
                            )}
                        />

                        <div className='project-adjustment-arrow' aria-hidden='true'>
                            <ArrowForwardRoundedIcon />
                        </div>

                        <TextField
                            className='project-adjustment-value-field'
                            label='Valor do ajuste'
                            id='valor'
                            required
                            type='number'
                            value={valor}
                            onChange={(e) => setValor(e.target.value)}
                            inputProps={{ step: '0.01' }}
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>R$</InputAdornment>
                            }}
                            helperText='Positivo para conta a receber. Negativo para conta a pagar.'
                        />

                        <div className='project-adjustment-arrow' aria-hidden='true'>
                            <ArrowForwardRoundedIcon />
                        </div>

                        <Autocomplete
                            className='project-adjustment-field'
                            id='projetoFinal'
                            options={projetosList}
                            value={selectedProjetoFinal}
                            onChange={(_, value) => setSelectedProjetoFinal(value)}
                            getOptionLabel={(option) => option.NomeProjeto}
                            isOptionEqualToValue={(option, value) => option.CodigoProjeto === value.CodigoProjeto}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Projeto destino'
                                    required
                                    helperText='Projeto final do lançamento contábil.'
                                />
                            )}
                        />
                    </div>

                    <TextField
                        className='project-adjustment-notes'
                        label='Observação'
                        id='observacao'
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        value={observacao}
                        onChange={(e) => setObservacao(e.target.value)}
                        placeholder='Registre o motivo do ajuste para auditoria futura.'
                    />
                </section>

                <aside className='project-adjustment-summary'>
                    <div className='project-adjustment-summary-header'>
                        <AccountTreeRoundedIcon />
                        <div>
                            <span>Resumo</span>
                            <strong>Revisão do lançamento</strong>
                        </div>
                    </div>

                    <div className='project-adjustment-summary-list'>
                        <div>
                            <span>Origem</span>
                            <strong>{selectedProjetoInicial?.NomeProjeto || 'Selecione um projeto'}</strong>
                        </div>
                        <div>
                            <span>Destino</span>
                            <strong>{selectedProjetoFinal?.NomeProjeto || 'Selecione um projeto'}</strong>
                        </div>
                        <div>
                            <span>Valor</span>
                            <strong className={adjustmentType.className}>{formattedValue}</strong>
                        </div>
                        <div>
                            <span>Tipo</span>
                            <strong className={adjustmentType.className}>{adjustmentType.label}</strong>
                        </div>
                        <div>
                            <span>Observação</span>
                            <strong>{observacao || 'Sem observação'}</strong>
                        </div>
                    </div>

                    <div className='project-adjustment-guidance'>
                        <NotesRoundedIcon />
                        <p>Confirme origem, destino e sinal do valor antes do envio.</p>
                    </div>

                    <SubmitButton isSubmit={isSubmit} startIcon={<PaidRoundedIcon />}>Lançar Ajuste</SubmitButton>
                </aside>
            </form>
        </Box>
    );
};

export default AjusteProjetoConsorcio;
